import { jwtDecode } from 'jwt-decode';

/**
 * JWT payload structure from Argus IAM API
 */
export interface ArgusJwtPayload {
  sub: string; // user UUID
  email: string;
  name?: string;
  tenant?: string; // tenant identifier
  tenant_id: string;
  tenant_slug: string;
  scopes?: string[]; // e.g., ["tenant:acme", "tenant:acme:app:smartsite"]
  permissions?: string[]; // e.g., ["site.view", "site.edit"]
  roles?: string[];
  iss: string; // issuer (Argus domain)
  aud?: string; // audience (app slug)
  exp: number; // expiration timestamp
  iat: number; // issued at timestamp
  kid?: string; // key ID
}

/**
 * Parse JWT token without verification (client-side only)
 * Note: Signature validation happens server-side
 */
export function parseJwt(token: string): ArgusJwtPayload | null {
  try {
    const decoded = jwtDecode<ArgusJwtPayload>(token);
    return decoded;
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Get JWT from cookie
 * Note: With httpOnly cookies, this may not work. Backend should decode JWT.
 * This is a fallback for non-httpOnly tokens.
 */
export function getJwtFromCookie(cookieName: string = 'argus_token'): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Check if JWT is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    return true;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
}

/**
 * Get user permissions from JWT
 */
export function getUserPermissions(token: string): string[] {
  const payload = parseJwt(token);
  return payload?.permissions || [];
}

/**
 * Get user info from JWT
 */
export function getCurrentUser(token: string): {
  id: string;
  email: string;
  name?: string;
} | null {
  const payload = parseJwt(token);
  if (!payload) return null;
  
  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
  };
}

/**
 * Get current tenant info from JWT
 */
export function getCurrentTenant(token: string): {
  id: string;
  slug: string;
} | null {
  const payload = parseJwt(token);
  if (!payload) return null;
  
  return {
    id: payload.tenant_id,
    slug: payload.tenant_slug,
  };
}

/**
 * Check if user has specific permission
 */
export function hasPermission(token: string, permission: string): boolean {
  const permissions = getUserPermissions(token);
  return permissions.includes(permission);
}

/**
 * Get all user roles from JWT
 */
export function getUserRoles(token: string): string[] {
  const payload = parseJwt(token);
  return payload?.roles || [];
}

/**
 * Get user scopes from JWT (hierarchical authorization boundaries)
 */
export function getUserScopes(token: string): string[] {
  const payload = parseJwt(token);
  return payload?.scopes || [];
}
